import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { LogOut, LayoutDashboard, ShoppingBag, Users, Boxes, Mail, Star, Send, FileSpreadsheet, History, ExternalLink, PenLine, Package, Radio, Shield, Bot, Bell, Paintbrush, SlidersHorizontal, Tag, Rocket } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { get, put, inputCls, fmtDate } from "../components/admin/shared";
import { Overview } from "../components/admin/Overview";
import { Orders } from "../components/admin/Orders";
import { Customers } from "../components/admin/Customers";
import { Stock } from "../components/admin/Stock";
import { Messages } from "../components/admin/Messages";
import { Reviews } from "../components/admin/Reviews";
import { Newsletter } from "../components/admin/Newsletter";
import { Accounting } from "../components/admin/Accounting";
import { Activity } from "../components/admin/Activity";
import { Content } from "../components/admin/Content";
import { ProductsAdmin } from "../components/admin/ProductsAdmin";
import { Live } from "../components/admin/Live";
import { Security, ChatAdmin } from "../components/admin/Security";
import { SiteEditor } from "../components/admin/SiteEditor";
import { Settings } from "../components/admin/Settings";
import { Coupons } from "../components/admin/Coupons";
import { Launch } from "../components/admin/Launch";

const NAV = [
  { id: "apercu", label: "Aperçu", icon: LayoutDashboard },
  { id: "live", label: "Trafic live", icon: Radio },
  { id: "produits", label: "Produits", icon: Package },
  { id: "commandes", label: "Commandes", icon: ShoppingBag, badge: (s) => s?.pending_orders },
  { id: "clients", label: "Clients", icon: Users },
  { id: "stock", label: "Stock", icon: Boxes, badge: (s) => s?.low_stock?.length },
  { id: "messages", label: "Messages", icon: Mail, badge: (s) => s?.messages_unread },
  { id: "avis", label: "Avis", icon: Star, badge: (s) => s?.reviews_pending },
  { id: "promos", label: "Codes promo", icon: Tag },
  { id: "lancement", label: "Précommande / Lancement", icon: Rocket },
  { id: "site", label: "Éditeur du site", icon: Paintbrush },
  { id: "contenu", label: "Pages & boutique", icon: PenLine },
  { id: "reglages", label: "Réglages", icon: SlidersHorizontal },
  { id: "newsletter", label: "Newsletter", icon: Send },
  { id: "comptabilite", label: "Comptabilité", icon: FileSpreadsheet },
  { id: "chat", label: "Chat IA", icon: Bot },
  { id: "journal", label: "Journal", icon: History },
  { id: "securite", label: "Sécurité", icon: Shield },
];

const Login = ({ login, verify2fa }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pre, setPre] = useState(null);
  const [code, setCode] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (pre) {
        const data = await verify2fa(pre, code);
        if (data.role !== "admin") toast.error("Ce compte n'est pas administrateur.");
        return;
      }
      const data = await login(form.email, form.password);
      if (data.requires_2fa) {
        setPre(data.pre_token);
        if (data.email_sent) toast.message("Code de vérification requis — application d'authentification ou code reçu par email");
        else toast.message("Code de vérification requis — utilisez votre application d'authentification (email non délivrable)");
        return;
      }
      if (data.role !== "admin") toast.error("Ce compte n'est pas administrateur.");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (!err.response) toast.error("Serveur injoignable — vérifiez votre connexion et réessayez dans un instant");
      else if (typeof d === "string") toast.error(d);
      else toast.error(err.response.status >= 500 ? `Erreur serveur (${err.response.status}) — réessayez dans un instant` : "Identifiants incorrects");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen pt-32 pb-24 px-6 flex justify-center" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-sm">
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-3">Espace privé</p>
        <h1 className="font-serif text-4xl text-stone-900 mb-8">Administration</h1>
        <form onSubmit={submit} className="space-y-4">
          {pre ? (
            <div className="space-y-3" data-testid="admin-2fa-step">
              <p className="text-sm text-stone-600">Saisissez le code à 6 chiffres de votre application d'authentification, ou celui reçu par email.</p>
              <input data-testid="admin-2fa-code" autoFocus required maxLength={6} className={`${inputCls} w-full py-3 font-mono tracking-[0.4em] text-center text-lg`} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            </div>
          ) : (<>
          <input data-testid="admin-email-input" required type="email" className={`${inputCls} w-full py-3`} placeholder="Email admin" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="admin-password-input" required type="password" className={`${inputCls} w-full py-3`} placeholder="Mot de passe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </>)}
          <button data-testid="admin-login-button" type="submit" disabled={busy} className="w-full rounded-full bg-stone-900 text-[#FAF7F2] py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-[#C85A32] transition-colors duration-300 disabled:opacity-60">
            {busy ? "..." : pre ? "Vérifier le code" : "Se connecter"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default function AdminPage() {
  const { user, login, logout, verify2fa, refreshUser } = useAuth();
  const [tab, setTab] = useState("apercu");
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notif, setNotif] = useState({ unread: 0, items: [] });
  const [bell, setBell] = useState(false);
  const unreadRef = useRef(0);
  unreadRef.current = notif.unread;

  const refresh = useCallback(() => {
    get("/stats").then(setStats).catch(() => {});
    get("/settings").then(setSettings).catch(() => {});
    get("/notifications").then(setNotif).catch(() => {});
  }, []);

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user, refresh]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const iv = setInterval(() => get("/notifications").then((n) => { if (n.unread > unreadRef.current) toast.message(n.items[0]?.title, { description: n.items[0]?.body }); setNotif(n); }).catch(() => {}), 15000);
    return () => clearInterval(iv);
  }, [user]);
  const openBell = async () => { setBell(!bell); if (!bell && notif.unread) { await put("/notifications/read", {}); setNotif((n) => ({ ...n, unread: 0, items: n.items.map((i) => ({ ...i, read: true })) })); } };

  if (user === null) return <div className="min-h-screen pt-40 text-center text-stone-500 font-mono text-sm">…</div>;
  if (!user || user.role !== "admin") return <Login login={login} verify2fa={verify2fa} />;

  const current = NAV.find((n) => n.id === tab);

  return (
    <div className="min-h-screen pt-24 lg:pt-28 pb-16 px-4 lg:px-8 max-w-[1500px] mx-auto" data-testid="admin-dashboard">
      <div className="grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-6 min-w-0">
        <aside className="lg:sticky lg:top-28 self-start rounded-2xl bg-[#1C1917] text-[#EFEAE1] p-4 lg:p-5 min-w-0 w-full lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto" data-lenis-prevent data-testid="admin-sidebar">
          <div className="hidden lg:block mb-6 px-2">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C85A32]">Tableau de bord</p>
            <p className="font-serif text-2xl tracking-[0.2em] mt-1">NŪRA</p>
            <p className="text-[10px] font-mono text-[#EFEAE1]/40 mt-1 truncate">{user.email}</p>
          </div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible w-full">
            {NAV.map(({ id, label, icon: Icon, badge }) => {
              const b = badge?.(stats);
              return (
                <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-[12px] uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${tab === id ? "bg-[#EFEAE1] text-stone-900" : "text-[#EFEAE1]/70 hover:bg-white/5 hover:text-[#EFEAE1]"}`}>
                  <Icon size={15} /> <span className="flex-1 text-left">{label}</span>
                  {b > 0 && <span className={`rounded-full px-2 py-0.5 text-[10px] font-mono ${tab === id ? "bg-[#C85A32] text-white" : "bg-[#C85A32]/80 text-white"}`} data-testid={`admin-badge-${id}`}>{b}</span>}
                </button>
              );
            })}
          </nav>
          <div className="hidden lg:block mt-6 pt-5 border-t border-white/10 space-y-2 px-2">
            <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[11px] text-[#EFEAE1]/50 hover:text-[#EFEAE1]"><ExternalLink size={12} /> Dashboard Stripe</a>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-[11px] text-[#EFEAE1]/50 hover:text-[#C85A32]"><LogOut size={12} /> Déconnexion</button>
          </div>
        </aside>

        <main className="min-w-0">
          {settings?.site_mode && settings.site_mode !== "live" && <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-900" data-testid="admin-mode-banner">Le site public est en mode <b>{settings.site_mode === "maintenance" ? "maintenance" : settings.site_mode === "launch" ? "précommande / lancement (page d'accueil = page de lancement)" : "coming soon"}</b>{settings.site_mode !== "launch" && " — seuls vous et les personnes ayant le mot de passe de prévisualisation le voient."} <button onClick={() => setTab("reglages")} className="underline">Modifier</button></div>}
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-2">Administration</p>
              <h1 className="font-serif text-3xl lg:text-4xl text-stone-900">{current?.label}</h1>
            </div>
            <div className="relative">
              <button data-testid="admin-bell" onClick={openBell} className="relative w-10 h-10 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 hover:border-stone-900">
                <Bell size={16} />{notif.unread > 0 && <span data-testid="admin-bell-count" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#C85A32] text-white text-[10px] font-mono flex items-center justify-center">{notif.unread}</span>}
              </button>
              {bell && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl border border-stone-200 bg-white shadow-xl z-30" data-lenis-prevent data-testid="admin-notifications">
                  {notif.items.length === 0 ? <p className="p-4 text-sm text-stone-500">Aucune notification.</p> : notif.items.slice(0, 30).map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b border-stone-100 ${n.read ? "" : "bg-[#FDF6F0]"}`}><p className="text-sm text-stone-900">{n.title}</p><p className="text-xs text-stone-500">{n.body}</p><p className="text-[10px] font-mono text-stone-400 mt-1">{fmtDate(n.at, true)}</p></div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={logout} className="lg:hidden inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-stone-600"><LogOut size={13} /> Quitter</button>
          </div>

          {tab === "apercu" && <Overview stats={stats} goTo={setTab} />}
          {tab === "live" && <Live />}
          {tab === "produits" && <ProductsAdmin onChanged={refresh} />}
          {tab === "chat" && <ChatAdmin />}
          {tab === "securite" && <Security user={user} refreshUser={refreshUser} settings={settings} onSettings={setSettings} />}
          {tab === "site" && <SiteEditor />}
          {tab === "promos" && <Coupons />}
          {tab === "lancement" && <Launch settings={settings} onSettings={setSettings} />}
          {tab === "reglages" && settings && <Settings settings={settings} onSettings={setSettings} />}
          {tab === "commandes" && <Orders onChanged={refresh} />}
          {tab === "clients" && <Customers />}
          {tab === "stock" && <Stock stats={stats} settings={settings} onSaved={refresh} onSettings={setSettings} />}
          {tab === "messages" && <Messages onChanged={refresh} />}
          {tab === "avis" && <Reviews onChanged={refresh} />}
          {tab === "contenu" && <Content settings={settings} onSettings={setSettings} />}
          {tab === "newsletter" && <Newsletter />}
          {tab === "comptabilite" && settings && <Accounting settings={settings} onSettings={setSettings} />}
          {tab === "journal" && <Activity />}
        </main>
      </div>
    </div>
  );
}
